import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import './PoliciesPage.css';

const SECTIONS = [
  { id: 'cookies', label: 'Política de cookies' },
  { id: 'privacidad', label: 'Aviso de privacidad' },
  { id: 'envios', label: 'Política de envíos y devoluciones' },
];

const PoliciesPage = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('cookies');

  // Deep link: /policies?section=envios
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && SECTIONS.some(s => s.id === section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Scroll to top on section change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection]);

  return (
    <div className="policies-page" data-header-theme="light">
      <SEO
        title="Políticas — Cookies, privacidad, envíos y devoluciones"
        description="Políticas de Physiology Skincare Co: aviso de privacidad, política de cookies, condiciones de envío y proceso de devoluciones."
        path="/policies"
      />
      {/* Sidebar */}
      <aside className="policies-sidebar">
        <nav>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              className={`policies-sidebar__link ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="policies-content">

        {/* Política de Cookies */}
        {activeSection === 'cookies' && (
          <section className="policy-section">
            <h2>Política de cookies</h2>
            <p>En Physiology utilizamos cookies y tecnologías similares para mejorar tu experiencia de navegación, analizar el uso del sitio y personalizar el contenido que te mostramos.</p>

            <h3>¿Qué son las cookies?</h3>
            <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas nuestro sitio. Nos permiten reconocerte en visitas posteriores y recordar tus preferencias.</p>

            <h3>Tipos de cookies que utilizamos</h3>
            <table>
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Finalidad</th>
                  <th>Duración</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Esenciales</td>
                  <td>Necesarias para el funcionamiento del sitio, como el carrito de compra y la sesión.</td>
                  <td>Sesión</td>
                </tr>
                <tr>
                  <td>Analíticas</td>
                  <td>Nos permiten entender cómo los usuarios interactúan con el sitio.</td>
                  <td>2 años</td>
                </tr>
                <tr>
                  <td>Marketing</td>
                  <td>Usadas para mostrarte publicidad relevante dentro y fuera de nuestro sitio.</td>
                  <td>1 año</td>
                </tr>
                <tr>
                  <td>Preferencias</td>
                  <td>Recuerdan tus configuraciones como idioma y región.</td>
                  <td>1 año</td>
                </tr>
              </tbody>
            </table>

            <h3>¿Cómo controlar las cookies?</h3>
            <p>Puedes gestionar tus preferencias de cookies en cualquier momento desde el panel de configuración disponible en la parte inferior derecha de esta página. También puedes configurar tu navegador para rechazar todas las cookies, aunque esto puede afectar la funcionalidad del sitio.</p>

            <div className="policy-divider" />
            <p>Última actualización: marzo 2026.</p>
          </section>
        )}

        {/* Aviso de Privacidad */}
        {activeSection === 'privacidad' && (
          <section className="policy-section">
            <h2>Aviso de privacidad</h2>
            <p>Physiology, S.A. de C.V. (en adelante "Physiology") es responsable del tratamiento de tus datos personales conforme a lo establecido en la Ley Federal de Protección de Datos Personales en Posesión de los Particulares.</p>

            <h3>Datos que recopilamos</h3>
            <ul>
              <li>Nombre completo y datos de contacto (correo electrónico, teléfono)</li>
              <li>Dirección de envío y facturación</li>
              <li>Historial de compras y preferencias de productos</li>
              <li>Datos de navegación mediante cookies</li>
            </ul>

            <h3>Finalidades del tratamiento</h3>
            <p>Utilizamos tu información para procesar pedidos, gestionar tu cuenta, enviarte comunicaciones sobre tu compra, y con tu consentimiento, enviarte comunicaciones comerciales y personalizar tu experiencia.</p>

            <h3>Transferencia de datos</h3>
            <p>Podemos compartir tus datos con proveedores de servicios de logística y procesamiento de pagos necesarios para completar tu compra. No vendemos tu información a terceros.</p>

            <h3>Tus derechos (ARCO)</h3>
            <p>Tienes derecho a Acceder, Rectificar, Cancelar u Oponerte al tratamiento de tus datos personales. Para ejercer estos derechos puedes escribirnos a <strong>hello@physiologyskincare.com</strong>.</p>

            <div className="policy-divider" />
            <p>Última actualización: marzo 2026.</p>
          </section>
        )}

        {/* Envíos y Devoluciones */}
        {activeSection === 'envios' && (
          <section className="policy-section">
            <h2>Política de envíos y devoluciones</h2>
            <p>En Physiology queremos que tu experiencia de compra sea impecable de principio a fin. A continuación encontrarás toda la información sobre tiempos, costos y condiciones de envío y devolución.</p>

            <h3>Tiempos de envío</h3>
            <table>
              <thead>
                <tr>
                  <th>Zona</th>
                  <th>Tiempo estimado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CDMX y Zona Metropolitana</td>
                  <td>1–2 días hábiles</td>
                </tr>
                <tr>
                  <td>República Mexicana</td>
                  <td>3–5 días hábiles</td>
                </tr>
                <tr>
                  <td>Internacional</td>
                  <td>7–14 días hábiles</td>
                </tr>
              </tbody>
            </table>

            <h3>Costos de envío</h3>
            <p>
              Consulta el costo de envío a tu zona desde nuestro{' '}
              <button
                type="button"
                className="policy-inline-link"
                onClick={() => window.dispatchEvent(new Event('phys:open-chat'))}
              >
                chat
              </button>
              .
            </p>

            <h3>Política de devoluciones</h3>
            <p>Aceptamos devoluciones dentro de los <strong>30 días naturales</strong> a partir de la fecha de recepción, siempre que el producto se encuentre en su estado original, sin abrir y con empaque intacto.</p>

            <h3>Proceso de devolución</h3>
            <ul>
              <li>Escríbenos a <strong>hello@physiologyskincare.com</strong> con tu número de pedido</li>
              <li>Te enviaremos una guía de devolución prepagada</li>
              <li>Una vez recibido el producto, procesamos el reembolso en 5–7 días hábiles</li>
            </ul>

            <h3>Productos no elegibles para devolución</h3>
            <ul>
              <li>Productos con sello de garantía roto o parcialmente utilizados</li>
              <li>Productos en oferta o de venta final</li>
              <li>Pedidos con más de 30 días desde la entrega</li>
            </ul>

            <div className="policy-divider" />
            <p>Última actualización: marzo 2026.</p>
          </section>
        )}
      </main>
    </div>
  );
};

export default PoliciesPage;
